import { Dropdown, Popover } from 'antd';
import classNames from 'classnames';
import { noop } from 'lodash-es';
import { createContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { useNodeInnerText } from '../../common/utils/stringUtils';
import { joinReactElements } from '../../utils/reactUtils';
import Whitespace from '../Whitespace';
import { FAIcon } from '../adapters/fontAwesomeAdapters';
import { VerticalDivider } from '../layout/layoutElements';
import { useResponsiveQueries } from '../responsive/responsiveQueries';
import { useNavElementsStyles } from './navElements.styles';

function renderLink(link) {
  if (link.href) {
    return (
      <a
        key={link.textId || link.text}
        href={link.href}
        target="__blank"
        {...link.props}
      >
        {link.textId ? <FormattedMessage id={link.textId} /> : link.text}
      </a>
    );
  }
  if (link.to) {
    return (
      <Link key={link.textId || link.text} to={link.to} {...link.props}>
        {link.textId ? <FormattedMessage id={link.textId} /> : link.text}
      </Link>
    );
  }
  return link;
}

export function LinksRow({ links, className, ...rest }) {
  const { styles } = useNavElementsStyles();
  return (
    <div
      className={classNames('LinksRow', styles.linksRow, className)}
      {...rest}
    >
      {joinReactElements(links.map(renderLink), i => (
        <VerticalDivider key={i} className={styles.linksRowDivider} />
      ))}
    </div>
  );
}

export const PopupContext = createContext({ isOpen: false, setOpen: noop });

export function Popup({
  labelId,
  label,
  trigger,
  placement,
  titleId,
  title,
  customOpenIcon,
  customClosedIcon,
  noIcon,
  children,
  className,
  disabled,
  rootClassName,
  ...rest
}) {
  const [isOpen, setOpen] = useState();
  const { ref, text } = useNodeInnerText();
  const { styles } = useNavElementsStyles();

  return (
    <PopupContext.Provider value={{ isOpen, setOpen }}>
      <Popover
        className={classNames(
          'Popup-Label',
          styles.dropdownMenuLabel,
          className
        )}
        classNames={rootClassName ? { root: rootClassName } : undefined}
        title={titleId ? <FormattedMessage id={titleId} /> : title}
        trigger={disabled ? 'none' : trigger}
        placement={placement}
        content={children}
        open={isOpen}
        onOpenChange={setOpen}
        {...rest}
      >
        <div ref={ref} title={text}>
          {labelId ? <FormattedMessage id={labelId} /> : label}
          {!noIcon && (
            <>
              <Whitespace />
              {isOpen
                ? customOpenIcon || <FAIcon icon="chevron-up" />
                : customClosedIcon || <FAIcon icon="chevron-down" />}
            </>
          )}
        </div>
      </Popover>
    </PopupContext.Provider>
  );
}

export function DropdownMenu({
  labelId,
  label,
  trigger,
  placement = 'bottomRight',
  className,
  overlayClassName,
  noArrow,
  items,
  ...rest
}) {
  const media = useResponsiveQueries();
  const isSm = media.sm || media.xs;
  const isMd = media.md;
  const triggerSubMenuAction = isSm || isMd ? 'click' : 'hover';
  const { styles } = useNavElementsStyles();

  return (
    <Dropdown
      className={classNames(
        'DropdownMenu-Label',
        styles.dropdownMenuLabel,
        className
      )}
      menu={{
        items,
        expandIcon: <FAIcon icon="angle-right" />,
        triggerSubMenuAction,
      }}
      overlayClassName={classNames(
        styles.dropdownMenuOverlay,
        styles.dropdownThemeAlt,
        overlayClassName
      )}
      trigger={trigger}
      placement={placement}
      {...rest}
    >
      <div>
        {labelId ? <FormattedMessage id={labelId} /> : label}
        {!noArrow && (
          <>
            <Whitespace />
            <FAIcon icon="caret-down" />
          </>
        )}
      </div>
    </Dropdown>
  );
}
